import { AccordionContent, AccordionItem, AccordionTrigger, Checkbox } from '@bridebook/aura';
import { IGuest } from '@bridebook/models/source/models/Weddings/Guests.types';
import { getGuests } from '@bridebook/web/lib/guestlist/selectors';
import { useRecipientsContext } from '@bridebook/web/lib/inbox/guests/create-message-flow/recipients/recipients-context';
import {
  filterSelectedGuests,
  getGuestsMap,
  getGuestsWithEmail,
} from '@bridebook/web/lib/inbox/guests/guests-helpers';
import { useSelector } from '@bridebook/web/lib/utils';
import { isEqual } from 'lodash';
import { memo, useCallback, useMemo } from 'react';

import { TeamCheckboxes } from './team-checkboxes';

export interface IGroup {
  id: string;
  label: string;
  teams: IGuest[][];
}

interface IProps {
  label: string;
  group: IGroup;
  onSelect: (guestId: string, selected: boolean) => void;
  selectedGuestIds: Set<string>;
}

const SingleGroupComponent = ({ group, onSelect, selectedGuestIds, label }: IProps) => {
  const guestsWithEmail = useMemo(() => getGuestsWithEmail(group.teams), [group.teams]);
  const { setGuestIdForEmailEdit } = useRecipientsContext();
  const guestsInGroup = useMemo(() => group.teams.flat(), [group.teams]);

  const isAllWithEmailSelected = useMemo(() => {
    if (guestsWithEmail.length === 0) return false;
    return (
      filterSelectedGuests(guestsWithEmail, selectedGuestIds).length === guestsWithEmail.length
    );
  }, [guestsWithEmail, selectedGuestIds]);

  const handleToggleAll = useCallback(() => {
    const shouldSelect = !isAllWithEmailSelected;
    const guestsToToggle = guestsWithEmail.filter((guest) =>
      shouldSelect ? !selectedGuestIds.has(guest.id) : selectedGuestIds.has(guest.id),
    );

    guestsToToggle.forEach((guest) => onSelect(guest.id, shouldSelect));
  }, [guestsWithEmail, isAllWithEmailSelected, onSelect, selectedGuestIds]);

  const guests = useSelector(getGuests);
  const guestsMap = useMemo(() => getGuestsMap(guests), [guests]);

  const getGuestEmail = useCallback(
    (guestId: string) => guestsMap[guestId]?.contacts?.email || '',
    [guestsMap],
  );

  const getGuestName = useCallback(
    (guestId: string) => guestsMap[guestId]?.name || '',
    [guestsMap],
  );

  const { selectedCount, totalCount } = useMemo(
    () => ({
      selectedCount: filterSelectedGuests(guestsWithEmail, selectedGuestIds).length,
      totalCount: group.teams.reduce((acc, team) => acc + team.length, 0),
    }),
    [group.teams, guestsWithEmail, selectedGuestIds],
  );

  return (
    <AccordionItem value={group.id}>
      <AccordionTrigger asChild disabled={guestsInGroup.length === 0}>
        <div className="flex items-center gap-3">
          <div className="size-[24px]" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isAllWithEmailSelected}
              id={group.id}
              onCheckedChange={handleToggleAll}
              disabled={guestsWithEmail.length === 0}
            />
          </div>
          <label className="flex-1 text-left text-base font-normal text-space-75">
            {label} ({selectedCount}/{totalCount})
          </label>
        </div>
      </AccordionTrigger>
      {guestsInGroup.length > 0 && (
        <AccordionContent classNames={{ wrapper: 'px-0' }}>
          <div className="flex flex-col gap-4 pl-[32px]">
            {group.teams.map((team, index) => (
              <TeamCheckboxes
                key={index}
                items={team.map((guest) => ({
                  label: getGuestName(guest.id),
                  id: guest.id,
                  email: getGuestEmail(guest.id),
                  isSelected: selectedGuestIds.has(guest.id),
                }))}
                onSelect={onSelect}
                onEmailClick={setGuestIdForEmailEdit}
              />
            ))}
          </div>
        </AccordionContent>
      )}
    </AccordionItem>
  );
};

const arePropsEqual = (prevProps: IProps, nextProps: IProps) => {
  if ((prevProps.label, nextProps.label || prevProps.group.id !== nextProps.group.id)) {
    return false;
  }

  // Compare Sets by size and contents
  if (prevProps.selectedGuestIds.size !== nextProps.selectedGuestIds.size) {
    return false;
  }

  // Only check contents if sizes are equal
  for (const id of prevProps.selectedGuestIds) {
    if (!nextProps.selectedGuestIds.has(id)) {
      return false;
    }
  }

  if (isEqual(prevProps.group, nextProps.group)) {
    return false;
  }

  return true;
};

export const SingleGroup = memo(SingleGroupComponent, arePropsEqual);
